/**
 * Suy ra dữ liệu thử từ khối Kích hoạt của luồng: bấm "Chạy thử" là chạy ngay
 * luồng đang có trên canvas, không phải chọn sự kiện thủ công.
 * Ưu tiên sự kiện mẫu có sẵn; không có thì dựng một bản ghi tối thiểu theo đối
 * tượng, rồi ép các giá trị mà kích hoạt đòi ("đổi thành", lọc "Chỉ khi") để
 * lượt thử đi được qua khối Kích hoạt.
 */
import type { Connector, ConditionRuleV2, SampleEvent, SourceType } from './types'
import { GSHEET_SAMPLES, KIOTVIET_SAMPLES } from './kiotviet'
import { INTERNAL_SAMPLES, SCHEDULE_SAMPLES, type InternalObject } from './kich-hoat'

/** Bản ghi tối thiểu cho từng đối tượng CRM (dùng khi không có sự kiện mẫu khớp). */
export const MAU_BAN_GHI: Record<InternalObject, Record<string, unknown>> = {
  customer: { customer: { code: 'KH012345', name: 'Nguyễn Thị Mai', phone: '0912000111', lifecycleStage: 'customer', leadScore: 60, tags: ['Khách nhà thuốc'], memberRank: 'Hội viên', birthday: '1992-05-18', totalSpent: 1850000 } },
  conversation: { conversation: { id: 'cv_demo', channel: 'Zalo cá nhân', status: 'open', assignee: 'Thanh Hương', aiMode: 'auto', tags: [], isReplied: 'false', unreplied_hours: 3, lastMessage: 'Shop ơi cho mình hỏi sản phẩm này' }, customer: { name: 'Nguyễn Thị Mai', phone: '0912000111', memberRank: 'Hội viên' } },
  order: { order: { code: 'DH2026-0001', status: 'Hoàn thành', total: 520000, source: 'chat', items: [{ name: 'Vạn Phúc Trà - Hộp thiếc 100gr', category: 'Trà xanh', qty: 1 }] }, customer: { name: 'Nguyễn Thị Mai', phone: '0912000111', memberRank: 'Hội viên' } },
  task: { task: { title: 'Gọi tư vấn khách mới', status: 'open', assignee: 'Nhóm CSKH', dueAt: '2026-09-23T09:00:00' }, customer: { name: 'Nguyễn Thị Mai', phone: '0912000111' } },
  appointment: { appointment: { type: 'Tư vấn dược sĩ', status: 'confirmed', at: '2026-09-23T15:00:00', hours_until: 24 }, customer: { name: 'Nguyễn Thị Mai', phone: '0912000111' } },
}

function datSau(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.replace(/\[\]/g, '').split('.')
  let cur = obj
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof cur[parts[i]] !== 'object' || cur[parts[i]] == null) cur[parts[i]] = {}
    cur = cur[parts[i]] as Record<string, unknown>
  }
  cur[parts[parts.length - 1]] = value
}

/** Ép payload thoả các luật lọc đơn giản để lượt thử đi qua được khối Kích hoạt. */
function epLuat(payload: Record<string, unknown>, rules: ConditionRuleV2[]): Record<string, unknown> {
  const ra = structuredClone(payload)
  for (const r of rules) {
    if (!r.value) continue
    if (r.value.includes('{{now.')) { const d = new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 10); datSau(ra, r.field, r.value.replace('{{now.mmdd}}', d.slice(5)).replace('{{now.date}}', d)); continue }
    if (r.op === 'eq' || r.op === 'contains') datSau(ra, r.field, r.value)
    else if (r.op === 'in') datSau(ra, r.field, r.value.split(',')[0]?.trim() ?? r.value)
    else if (r.op === 'gte' || r.op === 'lte') datSau(ra, r.field, Number(r.value))
  }
  return ra
}

const clone = (s: SampleEvent, patch: Partial<SampleEvent>): SampleEvent => ({ ...s, ...patch })

/** Sự kiện mẫu khớp với khối Kích hoạt (dùng cho nút Chạy thử). */
export function suyRaSuKien(cfg: Record<string, unknown>, connectors: Connector[]): SampleEvent {
  const mode = String(cfg.mode ?? 'external')
  const loc = (cfg.filterRules as ConditionRuleV2[] | undefined) ?? []

  if (mode === 'internal') {
    const obj = String(cfg.object ?? 'customer') as InternalObject
    const ev = String(cfg.internalEvent ?? 'created')
    const field = String(cfg.field ?? '')
    const toValue = String(cfg.toValue ?? '').trim()
    const co = INTERNAL_SAMPLES.find((s) => {
      const dot = s.event.indexOf('.')
      const evObj = dot > 0 ? s.event.slice(0, dot) : s.event
      const [evKind, evField] = (dot > 0 ? s.event.slice(dot + 1) : '').split(':')
      return s.source === 'chatmql' && evObj === obj && evKind === ev && (!field || field === evField)
    })
    let payload = (co?.payload as Record<string, unknown>) ?? MAU_BAN_GHI[obj]
    payload = epLuat(payload, loc)
    if (ev === 'field_changed' && field && toValue) { payload = structuredClone(payload); datSau(payload, field, toValue) }
    if ((ev === 'tag_added' || ev === 'tag_removed') && !loc.length) { payload = structuredClone(payload); datSau(payload, `${obj}.tag_moi`, 'Khiếu nại') }
    const event = `${obj}.${ev}${ev === 'field_changed' && field ? `:${field}` : ''}`
    return co
      ? clone(co, { id: `thu_${obj}_${ev}`, event, payload })
      : { id: `thu_${obj}_${ev}`, source: 'chatmql', event, label: `Dữ liệu thử: ${obj}.${ev}`, payload }
  }

  if (mode === 'schedule') {
    const obj = String(cfg.object ?? 'customer') as InternalObject
    const cron = String(cfg.cron ?? '0 9 * * *')
    const goc = SCHEDULE_SAMPLES[0]
    const payload = epLuat({ ...(MAU_BAN_GHI[obj] ?? {}), schedule: { cron, ranAt: new Date().toISOString() } }, loc)
    return clone(goc, { id: 'thu_lich', event: 'cron', label: `Lịch ${cron} chạy — một bản ghi thoả điều kiện`, payload })
  }

  if (mode === 'manual') {
    const obj = String(cfg.object ?? 'customer') as InternalObject
    const nhan = String(cfg.buttonLabel || 'Chạy luồng')
    const payload = epLuat({ ...(MAU_BAN_GHI[obj] ?? {}), nguoiBam: 'Kim Ngân' }, loc)
    return { id: 'thu_thu_cong', source: 'chatmql', event: 'manual.button', label: `Nhân viên bấm nút "${nhan}"`, payload }
  }

  const loai = (connectors.find((x) => x.id === cfg.connectorId)?.type ?? 'webhook') as SourceType
  const suKien = String(cfg.event ?? '')
  const dsNgoai = loai === 'kiotviet' ? KIOTVIET_SAMPLES : loai === 'gsheet' ? GSHEET_SAMPLES : []
  const co = dsNgoai.find((s) => s.event === suKien) ?? dsNgoai[0]
  if (co) return co
  return { id: 'thu_webhook', source: loai, event: suKien || 'received', label: `Payload thử từ ${loai}`, payload: { name: 'Nguyễn Thị Mai', phone: '0912000111', note: 'Dữ liệu thử' } }
}
